<template>
  <main class="mypage-redesign">
    <!-- 메인 콘텐츠 영역 -->
    <div class="mypage-container">
      <!-- MypageSidebar 컴포넌트 -->
      <MypageSidebar />

      <!-- 메인 콘텐츠 -->
      <div class="main-content">
        <!-- 콘텐츠 보관함 섹션 -->
        <section class="contents-section">
          <div class="section-header">
            <div>
              <h2 class="section-title">콘텐츠 보관함</h2>
              <p class="section-description">구매한 콘텐츠를 확인하실 수 있습니다</p>
            </div>
          </div>

          <!-- 날짜 필터 -->
          <div class="filter-section">
            <div class="filter-row">
              <label class="filter-label">기간조회</label>
              <VDatePicker v-model.range="range" locale="ko" :max-date="new Date()">
                <template #default="{ inputValue, inputEvents }">
                  <div class="date-picker-wrapper">
                    <input type="text" class="date-input" :value="inputValue.start" v-on="inputEvents.start" />
                    <span class="date-separator">~</span>
                    <input type="text" class="date-input" :value="inputValue.end" v-on="inputEvents.end" />
                    <button class="search-button" type="button" @click="getMyContentsList(1)">조회</button>
                  </div>
                </template>
              </VDatePicker>
            </div>
          </div>

          <!-- 콘텐츠 그리드 -->
          <div v-if="myContentsList.length > 0">
            <div class="contents-grid">
              <div v-for="item in myContentsList" :key="item.orderItemId" class="content-card">
                <!-- 썸네일 -->
                <div class="content-thumbnail">
                  <img
                    v-if="item.thumbnailLink"
                    :src="`https://eduwind-api.nodeblack.net/${item.thumbnailLink}`"
                    :alt="item.orderItemTitle"
                  />
                  <img v-else src="@/assets/img/null_img.jpg" :alt="item.orderItemTitle" />

                  <!-- 콘텐츠 타입 배지 -->
                  <div
                    class="content-type-badge"
                    :class="{
                      'badge-image': item.contentType === 'IMAGE',
                      'badge-file': item.contentType === 'FILE',
                      'badge-video': item.contentType === 'VIDEO',
                      'badge-music': item.contentType === 'MUSIC'
                    }"
                  >
                    {{
                      item.contentType === 'IMAGE' ? '이미지' :
                      item.contentType === 'FILE' ? '파일' :
                      item.contentType === 'VIDEO' ? '영상' :
                      item.contentType === 'MUSIC' ? '음원' : ''
                    }}
                  </div>
                </div>

                <!-- 콘텐츠 정보 -->
                <div class="content-info">
                  <h3 class="content-title">{{ item.orderItemTitle }}</h3>

                  <!-- 기한 표시 -->
                  <p class="content-expiration">
                    <span v-if="new Date(item.downloadExpiration) < base" class="expiration-unlimited">무제한</span>
                    <span
                      v-else
                      :class="{
                        'expiration-active': !isExpired(item.downloadExpiration),
                        'expiration-expired': isExpired(item.downloadExpiration)
                      }"
                    >
                      {{ formDownloadDate(item.downloadExpiration) }} 까지
                      <span v-if="isExpired(item.downloadExpiration)" class="expired-label">기한만료</span>
                    </span>
                  </p>

                  <!-- 상태 표시 -->
                  <p v-if="item.orderItemStatus === 'NOT_PAID'" class="content-status">결제 대기 중</p>
                  <p v-else-if="item.orderItemStatus === 'CREATE_FAIL'" class="content-status error">시스템 오류 - 고객센터 문의</p>

                  <!-- 콘텐츠 이용 버튼 -->
                  <div class="content-actions">
                    <!-- FILE/IMAGE/MUSIC 타입 -->
                    <template v-if="item.contentType === 'FILE' || item.contentType === 'MUSIC' || item.contentType === 'IMAGE'">
                      <!-- 모두 다운로드 버튼 (2개 이상일 때) -->
                      <button
                        v-if="item.orderDownloadInfoList && item.orderDownloadInfoList.length > 1"
                        class="action-button primary download-all"
                        :class="{ 'loading': downloadingZips[item.orderItemId] }"
                        :disabled="downloadingZips[item.orderItemId]"
                        @click="downloadZIPContent(item.orderItemId)"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.46154 17H2.23077C1.90435 17 1.5913 16.8703 1.36048 16.6395C1.12967 16.4087 1 16.0956 1 15.7692V5.92303H17V15.7692C17 16.0956 16.8703 16.4087 16.6395 16.6395C16.4087 16.8703 16.0957 17 15.7692 17H14.5385" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M9 1V5.92308M14.28 1.61538C14.1747 1.43291 14.0244 1.2805 13.8434 1.17275C13.6624 1.06501 13.4567 1.00552 13.2462 1H4.75385C4.54326 1.00552 4.33762 1.06501 4.1566 1.17275C3.97559 1.2805 3.82526 1.43291 3.72 1.61538L1 5.92308H17L14.28 1.61538Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M6 13.0833L9 16M9 16L12 13.0833M9 16V9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        {{ downloadingZips[item.orderItemId] ? '준비중...' : '모두 다운로드' }}
                      </button>

                      <!-- 개별 다운로드 버튼 -->
                      <div class="download-buttons">
                        <button
                          v-for="(content, idx) in item.orderDownloadInfoList"
                          :key="content.orderDownloadId"
                          class="action-button secondary download-item"
                          :class="{ 'loading': downloadingFiles[content.orderDownloadId] }"
                          :disabled="downloadingFiles[content.orderDownloadId]"
                          @click="downloadContent(content.orderDownloadId, item.orderItemId)"
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 12.6667V16.2222C18 16.6937 17.8127 17.1459 17.4793 17.4793C17.1459 17.8127 16.6937 18 16.2222 18H3.77778C3.30628 18 2.8541 17.8127 2.5207 17.4793C2.1873 17.1459 2 16.6937 2 16.2222V12.6667M5.55556 8.22222L10 12.6667M10 12.6667L14.4444 8.22222M10 12.6667V2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          {{ idx + 1 }}. {{ content.orderDownloadName }} {{ downloadingFiles[content.orderDownloadId] ? '(준비중...)' : '' }}
                        </button>
                      </div>
                    </template>

                    <!-- VIDEO 타입 -->
                    <template v-else-if="item.contentType === 'VIDEO'">
                      <div v-for="vid in item.orderVideoInfoList" :key="vid.orderVideoId" class="video-buttons">
                        <button
                          @click.prevent="vid.linkNormal ? openVideoModal(vid.linkNormal) : null"
                          class="action-button primary"
                          :class="{ 'disabled': !vid.linkNormal }"
                          :disabled="!vid.linkNormal"
                        >
                          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6667 16.6667H2.08333V2.08333H16.6667M16.6667 0H2.08333C1.5308 0 1.00089 0.219493 0.610194 0.610194C0.219493 1.00089 0 1.5308 0 2.08333V16.6667C0 17.2192 0.219493 17.7491 0.610194 18.1398C1.00089 18.5305 1.5308 18.75 2.08333 18.75H16.6667C17.2192 18.75 17.7491 18.5305 18.1398 18.1398C18.5305 17.7491 18.75 17.2192 18.75 16.6667V2.08333C18.75 1.5308 18.5305 1.00089 18.1398 0.610194C17.7491 0.219493 17.2192 0 16.6667 0ZM7.29167 5.20833V13.5417L12.5 9.375L7.29167 5.20833Z" fill="currentColor"/>
                          </svg>
                          일반화질 보기
                        </button>

                        <button
                          v-if="vid.linkHigh"
                          @click.prevent="vid.linkHigh ? openVideoModal(vid.linkHigh) : null"
                          class="action-button secondary"
                          :class="{ 'disabled': !vid.linkHigh }"
                          :disabled="!vid.linkHigh"
                        >
                          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.7917 0.125H2.20833C1.05208 0.125 0.125 1.05208 0.125 2.20833V16.7917C0.125 17.9375 1.0625 18.875 2.20833 18.875H16.7917C17.9375 18.875 18.875 17.9375 18.875 16.7917V2.20833C18.875 1.6558 18.6555 1.12589 18.2648 0.735194C17.8741 0.344493 17.3442 0.125 16.7917 0.125ZM7.41667 13.6667V5.33333L12.625 9.5" fill="currentColor"/>
                          </svg>
                          고화질 보기
                        </button>
                      </div>
                    </template>
                  </div>

                  <!-- 관리 버튼 -->
                  <div class="content-management">
                    <router-link
                      v-if="(item.orderItemStatus != 'DOWNLOAD_FIN' && new Date(item.downloadExpiration) < base) || new Date(item.downloadExpiration) >= today"
                      :to="`/mypage/contentsBox/${item.orderItemId}`"
                      class="management-button detail"
                    >
                      상세보기
                      <svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.74999 15C6.74952 14.9015 6.76874 14.8038 6.80652 14.7128C6.8443 14.6218 6.89988 14.5392 6.96999 14.47L11.44 9.99999L6.99999 5.52999C6.9086 5.38904 6.86718 5.22148 6.88237 5.05418C6.89755 4.88687 6.96847 4.72951 7.08375 4.60731C7.19903 4.48512 7.35199 4.40516 7.51813 4.38026C7.68427 4.35536 7.85395 4.38696 7.99999 4.46999L13 9.47C13.1404 9.61062 13.2193 9.80124 13.2193 9.99999C13.2193 10.1987 13.1404 10.3894 13 10.53L7.99999 15.53C7.85936 15.6704 7.66874 15.7493 7.46999 15.7493C7.27124 15.7493 7.08061 15.6704 6.93999 15.53C6.8093 15.3853 6.74101 15.1948 6.74999 15Z" fill="currentColor"/>
                      </svg>
                    </router-link>

                    <button
                      v-if="item.reviewed === false"
                      @click="openModal(item)"
                      class="management-button review"
                    >
                      리뷰 작성
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 페이지네이션 -->
            <pagination v-if="pageInfo.page" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" />
          </div>

          <!-- 빈 상태 -->
          <div v-else class="empty-state">보관함이 비었습니다.</div>
        </section>
      </div>
    </div>

    <!-- 영상 재생 모달 -->
    <div v-if="isDetailModalOpen" class="video-modal">
      <div class="modal-overlay" @click="closeDetailModal"></div>
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="modal-close-button" @click="closeDetailModal">
            <span class="sr-only">닫기</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.062 4.062a.875.875 0 0 1 1.237 0L10 8.763l4.701-4.701a.875.875 0 1 1 1.237 1.237L11.237 10l4.701 4.701a.875.875 0 0 1-1.237 1.237L10 11.237l-4.701 4.701a.875.875 0 1 1-1.237-1.237L8.763 10 4.062 5.299a.875.875 0 0 1 0-1.237Z" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="selectedContentDetail" class="video-container">
            <section v-for="(section, idx) in selectedContentDetail" :key="idx">
              <template v-if="section.videoLinkNormal">
                <iframe
                  v-if="getVideoInfo(section.videoLinkNormal).type === 'youtube'"
                  :src="getVideoInfo(section.videoLinkNormal).src"
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                  class="video-iframe"
                ></iframe>

                <div v-else-if="getVideoInfo(section.videoLinkNormal).type === 'vimeo'" class="vimeo-wrapper">
                  <iframe
                    :src="getVideoInfo(section.videoLinkNormal).src"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                    class="video-iframe"
                  ></iframe>
                </div>

                <iframe v-else :src="section.videoLinkNormal" allowfullscreen class="video-iframe"></iframe>
              </template>
            </section>
          </div>
        </div>
      </div>
    </div>

    <!-- 리뷰 작성 모달 -->
    <ReviewWriteComponent v-if="modalVisible" @close="modalVisible = false" :content="modalData" />
  </main>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref, computed } from "vue";
import MypageSidebar from "@/components/MypageSidebar.vue";
import Pagination from "@/components/Pagination.vue";
import ReviewWriteComponent from "@/components/ReviewWriteComponent.vue";

const formatDate = (date) => date.toISOString().split("T")[0];

const start = new Date();
const end = new Date();
start.setDate(end.getDate() - 14);

const range = ref({ start, end });

const formStartDate = computed(() => formatDate(range.value.start));
const formEndDate = computed(() => formatDate(range.value.end));

const myContentsList = ref([]);
const pageInfo = ref({});
const page = ref(1);
const size = ref(10);

// 모달 관련 상태
const isDetailModalOpen = ref(false);
const selectedContentDetail = ref([]);

const getMyContentsList = async (num) => {
  try {
    const pageValue = num !== undefined ? 1 : page.value;
    const { data } = await axios.get(
      `/new_api/customer/order/item/list?page=${pageValue}&size=${size.value}&startDate=${formStartDate.value}&endDate=${formEndDate.value}`
    );
    myContentsList.value = data.data.orderItemList;
    pageInfo.value = data.data.pageInfo;
  } catch (err) {
    console.error(err);
  }
};

onMounted(async () => {
  await getMyContentsList();

  // Vimeo API 스크립트 로드
  if (!document.querySelector('script[src="https://player.vimeo.com/api/player.js"]')) {
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    document.head.appendChild(script);
  }
});

// 기한 관련
const today = new Date();
const base = new Date("2000-01-01");

const isExpired = (date) => {
  const expirationDate = new Date(date);
  return expirationDate < today;
};

const formDownloadDate = (d) => {
  const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" };
  const date = new Date(d);
  return date.toLocaleDateString("ko-KR", options).replace(/(\d+)\D(\d+)\D(\d+)\D(\d+)\D(\d+)/, "$1.$2.$3 $4:$5:$6");
};

const updatePage = (num) => {
  page.value = num;
  getMyContentsList();
};

// 리뷰 작성 모달
const modalVisible = ref(false);
const modalData = ref(null);

const openModal = (content) => {
  modalData.value = content;
  modalVisible.value = true;
};

// 영상 재생 모달
const openVideoModal = (url) => {
  if (!url) return;
  selectedContentDetail.value = [{ videoLinkNormal: url }];
  isDetailModalOpen.value = true;
};

const closeDetailModal = () => {
  isDetailModalOpen.value = false;
  selectedContentDetail.value = [];
};

// 영상 URL 파싱
const getVideoInfo = (url) => {
  if (!url) return { type: null };

  const trimmed = url.trim();

  // Vimeo 체크
  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    return {
      type: "vimeo",
      id: vimeoMatch[1],
      src: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
    };
  }

  // YouTube 체크
  const ytMatch =
    trimmed.match(/youtu\.be\/([^?&#]+)/i) ??
    trimmed.match(/youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)([^?&#]+)/i) ??
    trimmed.match(/[?&]v=([^?&#]+)/i);

  if (ytMatch && ytMatch[1]) {
    return {
      type: "youtube",
      id: ytMatch[1],
      src: `https://www.youtube.com/embed/${ytMatch[1]}`,
    };
  }

  return { type: null };
};

// 다운로드 상태 관리
const downloadingFiles = ref({});
const downloadingZips = ref({});

// 개별 다운로드
const downloadContent = async (orderDownloadId, orderItemId) => {
  if (downloadingFiles.value[orderDownloadId]) return;
  downloadingFiles.value[orderDownloadId] = true;
  try {
    const res = await axios.get(
      `/new_api/customer/order/content/download/one?orderContentId=${orderItemId}&orderDownloadId=${orderDownloadId}`,
      { responseType: "blob" }
    );

    const blob = new Blob([res.data], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const downloadLink = Object.assign(document.createElement("a"), {
      href: url,
      download: decodeURIComponent(
        res.headers["content-disposition"]
          .match(/filename\*?=(UTF-8[''].*'')?([^;]+)/)[2]
          .trim()
          .replace(/^"=\?UTF-8\?Q\?/, "")
          .replace(/\?="$/, "")
      ),
    });

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
  } finally {
    downloadingFiles.value[orderDownloadId] = false;
  }
};

// ZIP 다운로드
const downloadZIPContent = async (orderItemId) => {
  if (downloadingZips.value[orderItemId]) return;
  downloadingZips.value[orderItemId] = true;
  try {
    const res = await axios.get(`/new_api/customer/order/content/download/zip?orderContentId=${orderItemId}`, {
      responseType: "blob",
    });

    const blob = new Blob([res.data], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const downloadLink = Object.assign(document.createElement("a"), {
      href: url,
      download: decodeURIComponent(
        res.headers["content-disposition"]
          .match(/filename\*?=(UTF-8[''].*'')?([^;]+)/)[2]
          .trim()
          .replace(/^"=\?UTF-8\?Q\?/, "")
          .replace(/\?="$/, "")
      ),
    });

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
  } finally {
    downloadingZips.value[orderItemId] = false;
  }
};
</script>

<style scoped lang="scss">
/* ========================================
   Variables & Reset
   ======================================== */
.mypage-redesign {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0;
  font-family: "Pretendard", "Malgun Gothic", sans-serif;
}

/* ========================================
   Main Container
   ======================================== */
.mypage-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 40px;

  @media (max-width: 1023px) {
    grid-template-columns: 240px 1fr;
    gap: 32px;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    padding: 20px 16px 40px;
  }
}

/* ========================================
   Main Content
   ======================================== */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* ========================================
   Contents Section
   ======================================== */
.contents-section {
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #222;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 20px;
  }
}

.section-description {
  font-size: 14px;
  color: #888;
  margin: 0;
}

/* ========================================
   Filter Section
   ======================================== */
.filter-section {
  background: #f5f7fa;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    padding: 16px;
  }
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  min-width: 80px;

  @media (max-width: 767px) {
    min-width: auto;
  }
}

.date-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
}

.date-input {
  flex: 1;
  min-width: 140px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 8px;
  font-size: 14px;
  color: #555961;
  transition: all 0.2s;

  &:hover {
    border-color: #0c3388;
  }

  &:focus {
    outline: none;
    border-color: #0c3388;
    box-shadow: 0 0 0 3px rgba(12, 51, 136, 0.1);
  }

  @media (max-width: 767px) {
    min-width: 0;
  }
}

.date-separator {
  font-size: 14px;
  color: #555961;
  flex-shrink: 0;

  @media (max-width: 767px) {
    display: none;
  }
}

.search-button {
  padding: 12px 24px;
  background: #0c3388;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #092968;
  }

  &:focus-visible {
    outline: 2px solid #0c3388;
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
}

/* ========================================
   Contents Grid
   ======================================== */
.contents-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.content-card {
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.content-thumbnail {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f5f7fa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.content-type-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;

  &.badge-image {
    background: #8b5cf6;
  }

  &.badge-file {
    background: #3b82f6;
  }

  &.badge-video {
    background: #ef4444;
  }

  &.badge-music {
    background: #10b981;
  }
}

.content-info {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.content-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.content-expiration {
  font-size: 14px;
  margin: 0;

  .expiration-unlimited {
    color: #10b981;
    font-weight: 600;
  }

  .expiration-active {
    color: #555961;
  }

  .expiration-expired {
    color: #ef4444;
    font-weight: 600;
  }

  .expired-label {
    display: block;
    margin-top: 4px;
    font-size: 13px;
  }
}

.content-status {
  font-size: 14px;
  font-weight: 600;
  color: #f59e0b;
  margin: 0;

  &.error {
    color: #ef4444;
  }
}

.content-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &.primary {
    background: #0c3388;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #092968;
    }
  }

  &.secondary {
    background: #ffffff;
    border: 1px solid #dee0e3;
    color: #555961;

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #0c3388;
      color: #0c3388;
    }
  }

  &.loading {
    opacity: 0.6;
    cursor: wait;
  }

  &.disabled,
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
}

.download-all {
  width: 100%;
}

.download-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-item {
  width: 100%;
  font-size: 13px;
  padding: 8px 12px;
}

.video-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .action-button {
    width: 100%;
  }
}

.content-management {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.management-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  border: none;

  &.detail {
    background: #0c3388;
    color: #ffffff;

    &:hover {
      background: #092968;
    }

    svg {
      width: 15px;
      height: 20px;
    }
  }

  &.review {
    background: #ffffff;
    border: 1px solid #dee0e3;
    color: #555961;

    &:hover {
      background: #f9fafb;
      border-color: #0c3388;
      color: #0c3388;
    }
  }
}

/* ========================================
   Video Modal
   ======================================== */
.video-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
}

.modal-content {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1128px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-bottom: 1px solid #dee0e3;

  @media (max-width: 767px) {
    padding: 12px;
  }
}

.modal-close-button {
  padding: 8px;
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #222;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.video-container {
  width: 100%;
}

.video-iframe {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border: none;
}

.vimeo-wrapper {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;

  .video-iframe {
    width: 100%;
    height: 100%;
  }
}

/* ========================================
   Empty State
   ======================================== */
.empty-state {
  padding: 60px 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
}

/* ========================================
   Focus Styles
   ======================================== */
*:focus-visible {
  outline: 2px solid #0c3388;
  outline-offset: 2px;
}
</style>
